import React, { useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';

interface VoiceWaveformProps {
  isActive: boolean;
}

const WaveformContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '3px',
}));

const WaveBar = styled(Box)<{ height: number; isActive: boolean }>(({ theme, height, isActive }) => ({
  width: '3px',
  height: `${height}px`,
  background: isActive 
    ? 'linear-gradient(to top, #FF4500, #FF6347)'
    : 'rgba(255, 255, 255, 0.2)',
  borderRadius: '1px',
  transition: 'height 0.1s ease',
}));

const VoiceWaveform: React.FC<VoiceWaveformProps> = ({ isActive }) => {
  const animationRef = useRef<number | null>(null);
  const barsRef = useRef<Array<{ height: number; direction: 'up' | 'down' }>>([]);
  
  // Initialize bars with random heights
  useEffect(() => {
    const barCount = 30;
    barsRef.current = Array.from({ length: barCount }, () => ({
      height: Math.floor(Math.random() * 20) + 5,
      direction: Math.random() > 0.5 ? 'up' : 'down',
    }));
  }, []);

  // Animation loop for the waveform
  useEffect(() => {
    if (!isActive) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      return;
    }

    const animate = () => {
      barsRef.current = barsRef.current.map(bar => {
        let newHeight = bar.height;
        const change = Math.random() * 2 + 1;
        
        if (bar.direction === 'up') {
          newHeight += change;
          if (newHeight > 35) {
            return { height: newHeight, direction: 'down' };
          }
        } else {
          newHeight -= change;
          if (newHeight < 5) {
            return { height: newHeight, direction: 'up' };
          }
        }
        
        return { height: newHeight, direction: bar.direction };
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [isActive]);

  return (
    <WaveformContainer>
      {barsRef.current.map((bar, index) => (
        <WaveBar 
          key={index} 
          height={isActive ? bar.height : 5} 
          isActive={isActive}
        />
      ))}
    </WaveformContainer>
  );
};

export default VoiceWaveform; 